import type { PromptSubtopic } from '@/types/database-schema';

/** Shared setup contract; canonical reporting identities are assigned only on the server. */
export const MAX_PROJECT_PROMPTS = 1500;
export const MAX_PROMPT_GROUPS = 50;
export const MAX_PROMPTS_PER_GROUP = 250;
export const MAX_TOPIC_NAME = 80;
export const MAX_SUBTOPICS_PER_GROUP = 50;
export type ConfiguredPrompt = { text: string; stage: 'discovery' | 'decision' | 'trust' };
export type InventoryPrompt = string | ConfiguredPrompt;

/** Validate before a project reservation as well as before any SQL inventory write. */
export function normalizeConfiguredInventory(prompts: InventoryPrompt[]): ConfiguredPrompt[] {
  const configured = new Map<string, ConfiguredPrompt['stage']>();
  for (const prompt of prompts) {
    const text = (typeof prompt === 'string' ? prompt : prompt.text).trim();
    const stage = typeof prompt === 'string' ? 'discovery' : prompt.stage;
    if (!text || !['discovery', 'decision', 'trust'].includes(stage))
      throw new Error('Invalid configured prompt');
    if (configured.has(text) && configured.get(text) !== stage)
      throw new Error('The same prompt cannot have conflicting configured stages');
    configured.set(text, stage);
  }
  if (configured.size > MAX_PROJECT_PROMPTS)
    throw new Error('Configure at most 1500 active prompts');
  return [...configured].map(([text, stage]) => ({ text, stage }));
}

export type SetupPrompt = string | { ref: string; text: string };
export interface SetupTopic {
  name: string;
  prompt_refs: string[];
  subtopics?: Array<{ name: string; prompt_refs: string[] }>;
}
export interface NormalizedTopic {
  name: string;
  texts: string[];
  subtopics?: Array<{ name: string; texts: string[] }>;
}
export function normalizeTopicSetup(rawPrompts: unknown, rawGroups?: unknown) {
  if (!Array.isArray(rawPrompts)) throw new Error('prompts must be an array');
  if (rawPrompts.length > MAX_PROJECT_PROMPTS)
    throw new Error('A project supports at most 1500 active prompts');
  if (
    rawGroups !== undefined &&
    rawPrompts.some(p => !p || typeof p !== 'object' || typeof p.ref !== 'string')
  )
    throw new Error('Grouped setup requires a reference and text for every prompt');
  const refs = new Map<string, string>();
  const prompts = rawPrompts.map((item, index) => {
    const text =
      typeof item === 'string'
        ? item.trim()
        : typeof item?.text === 'string'
          ? item.text.trim()
          : '';
    if (!text) throw new Error(`Prompt ${index + 1} must contain text`);
    if (typeof item !== 'string' && item.ref !== undefined) {
      if (
        typeof item.ref !== 'string' ||
        !/^[a-zA-Z0-9_-]{1,32}$/.test(item.ref) ||
        refs.has(item.ref)
      )
        throw new Error(
          'Prompt references must be unique, 1–32 ASCII letters, digits, underscores or hyphens'
        );
      refs.set(item.ref, text);
    }
    return text;
  });
  if (new Set(prompts).size > MAX_PROJECT_PROMPTS)
    throw new Error('A project supports at most 1500 active prompts');
  if (rawGroups === undefined) return { prompts, groups: undefined };
  if (!Array.isArray(rawGroups) || rawGroups.length > MAX_PROMPT_GROUPS)
    throw new Error('A project supports at most 50 topics');
  const names = new Set<string>();
  const groups: NormalizedTopic[] = rawGroups.map(group => {
    const name = typeof group?.name === 'string' ? group.name.trim() : '';
    const key = name.toLocaleLowerCase('en-US');
    if (!name || name.length > MAX_TOPIC_NAME || names.has(key))
      throw new Error('Topic names must be unique and contain 1–80 characters');
    names.add(key);
    if (
      !Array.isArray(group.prompt_refs) ||
      !group.prompt_refs.length ||
      group.prompt_refs.length > MAX_PROMPTS_PER_GROUP
    )
      throw new Error('A topic requires 1–250 prompt references');
    const seen = new Set<string>();
    const texts: string[] = group.prompt_refs.map((ref: unknown) => {
      if (typeof ref !== 'string' || !refs.has(ref) || seen.has(ref))
        throw new Error('Topic references must be unique and refer to a supplied prompt');
      seen.add(ref);
      return refs.get(ref)!;
    });
    let subtopics: NormalizedTopic['subtopics'];
    if (group.subtopics !== undefined) {
      if (!Array.isArray(group.subtopics) || group.subtopics.length > MAX_SUBTOPICS_PER_GROUP)
        throw new Error('A topic supports at most 50 subtopics');
      const childNames = new Set<string>();
      subtopics = group.subtopics.map((rawChild: unknown) => {
        if (!rawChild || typeof rawChild !== 'object' || Array.isArray(rawChild))
          throw new Error('Each subtopic must be an object');
        const child = rawChild as Record<string, unknown>;
        const childName = typeof child?.name === 'string' ? child.name.trim() : '';
        const key = childName.toLocaleLowerCase('en-US');
        if (!childName || childName.length > MAX_TOPIC_NAME || childNames.has(key))
          throw new Error('Subtopic names must be unique and contain 1–80 characters');
        if (Object.keys(child).some(key => !['name', 'prompt_refs'].includes(key)))
          throw new Error('Subtopics support only name and prompt_refs');
        childNames.add(key);
        if (!Array.isArray(child.prompt_refs) || child.prompt_refs.length > MAX_PROMPTS_PER_GROUP)
          throw new Error('A subtopic supports 0–250 prompt references');
        const childRefs = new Set<string>();
        const childTexts = child.prompt_refs.map((ref: unknown) => {
          if (typeof ref !== 'string' || !seen.has(ref) || childRefs.has(ref))
            throw new Error('Subtopic references must be unique and belong to their parent topic');
          childRefs.add(ref);
          return refs.get(ref)!;
        });
        return { name: childName, texts: [...new Set(childTexts)] };
      });
    }
    return { name, texts: [...new Set(texts)], ...(subtopics !== undefined ? { subtopics } : {}) };
  });
  return { prompts, groups };
}
export const SETUP_PROMPTS_SCHEMA = {
  type: 'array',
  minItems: 1,
  maxItems: MAX_PROJECT_PROMPTS,
  items: {
    oneOf: [
      { type: 'string', minLength: 1 },
      {
        type: 'object',
        additionalProperties: false,
        required: ['ref', 'text'],
        properties: {
          ref: { type: 'string', pattern: '^[a-zA-Z0-9_-]{1,32}$' },
          text: { type: 'string', minLength: 1 },
        },
      },
    ],
  },
};
export const SETUP_TOPICS_SCHEMA = {
  type: 'array',
  maxItems: MAX_PROMPT_GROUPS,
  items: {
    type: 'object',
    additionalProperties: false,
    required: ['name', 'prompt_refs'],
    properties: {
      name: { type: 'string', minLength: 1, maxLength: MAX_TOPIC_NAME },
      subtopics: {
        type: 'array',
        maxItems: MAX_SUBTOPICS_PER_GROUP,
        items: {
          type: 'object',
          additionalProperties: false,
          required: ['name', 'prompt_refs'],
          properties: {
            name: { type: 'string', minLength: 1, maxLength: MAX_TOPIC_NAME },
            prompt_refs: {
              type: 'array',
              maxItems: MAX_PROMPTS_PER_GROUP,
              uniqueItems: true,
              items: { type: 'string' },
            },
          },
        },
      },
      prompt_refs: {
        type: 'array',
        minItems: 1,
        maxItems: MAX_PROMPTS_PER_GROUP,
        uniqueItems: true,
        items: { type: 'string' },
      },
    },
  },
};

/** Omission preserves children; an array is the complete desired child state. */
export function normalizePromptSubtopics(
  raw: unknown,
  parentIds: string[],
  existing?: PromptSubtopic[],
  options: { generateId?: () => string; promptField?: 'promptIds' | 'prompt_ids' } = {}
): PromptSubtopic[] | undefined {
  const value = raw === undefined ? existing : raw;
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length > MAX_SUBTOPICS_PER_GROUP)
    throw new Error('A topic supports at most 50 subtopics');
  const ids = new Set<string>(),
    names = new Set<string>(),
    parent = new Set(parentIds);
  return value.map(child => {
    if (!child || typeof child !== 'object' || Array.isArray(child))
      throw new Error('Each subtopic must be an object');
    const field = raw === undefined ? 'prompt_ids' : options.promptField || 'prompt_ids';
    if (Object.keys(child).some(key => !['id', 'name', field, 'created_at'].includes(key)))
      throw new Error('Subtopics support only id, name, prompt membership and creation metadata');
    const name = typeof child.name === 'string' ? child.name.trim() : '';
    const key = name.toLocaleLowerCase('en-US');
    const id =
      child.id === undefined
        ? existing?.find(prior => prior.name.toLocaleLowerCase('en-US') === key)?.id ||
          options.generateId?.()
        : typeof child.id === 'string'
          ? child.id.trim()
          : '';
    if (!id || id.length > 128 || ids.has(id))
      throw new Error('Subtopic IDs must be unique and contain 1–128 characters');
    if (!name || name.length > MAX_TOPIC_NAME || names.has(key))
      throw new Error('Subtopic names must be unique and contain 1–80 characters');
    if (
      !Array.isArray(child[field]) ||
      new Set(child[field]).size > MAX_PROMPTS_PER_GROUP ||
      child[field].some((id: unknown) => typeof id !== 'string' || !/^query-[a-f0-9]{16}$/.test(id))
    )
      throw new Error('A subtopic requires 0–250 distinct logical prompt IDs');
    const prompt_ids = [...new Set<string>(child[field])];
    if (prompt_ids.some(id => !parent.has(id)))
      throw new Error(
        'Subtopic prompts must belong to their parent topic; update children when removing parent prompts'
      );
    ids.add(id);
    names.add(key);
    const prior = existing?.find(child => child.id === id);
    let createdAt = child.created_at;
    // Real legacy Firestore timestamps normalize to the portable string contract.
    if (createdAt && typeof createdAt === 'object' && typeof createdAt.toDate === 'function')
      createdAt = createdAt.toDate().toISOString();
    if (
      createdAt !== undefined &&
      (typeof createdAt !== 'string' ||
        !/^\d{4}-\d{2}-\d{2}T.+(?:Z|[+-]\d{2}:\d{2})$/.test(createdAt) ||
        !Number.isFinite(Date.parse(createdAt)))
    )
      throw new Error('Subtopic creation metadata must be an ISO timestamp');
    const created_at: string | undefined = prior?.created_at ?? createdAt;
    return { id, name, prompt_ids, ...(created_at !== undefined ? { created_at } : {}) };
  });
}

export function promptSubtopicsResponse(children: PromptSubtopic[] | undefined) {
  return children?.map(child => ({
    id: child.id,
    name: child.name,
    promptIds: child.prompt_ids,
    ...(typeof child.created_at === 'string' ? { createdAt: child.created_at } : {}),
  }));
}

export function samePromptSubtopics(left?: PromptSubtopic[], right?: PromptSubtopic[]) {
  const comparable = (children?: PromptSubtopic[]) =>
    (children || [])
      .map(child => ({
        id: child.id,
        name: child.name,
        prompt_ids: [...child.prompt_ids].sort(),
      }))
      .sort((a, b) => a.id.localeCompare(b.id));
  return JSON.stringify(comparable(left)) === JSON.stringify(comparable(right));
}

export const MCP_SUBTOPICS_SCHEMA = {
  type: 'array',
  maxItems: MAX_SUBTOPICS_PER_GROUP,
  description:
    'Optional complete desired subtopics. Omit to preserve existing children; [] removes all. Children inherit parent ownership; each promptIds list is a subset of parent promptIds. New IDs are generated during preparation and returned in normalizedDraft.',
  items: {
    type: 'object',
    additionalProperties: false,
    required: ['name', 'promptIds'],
    properties: {
      id: { type: 'string', minLength: 1, maxLength: 128 },
      name: { type: 'string', minLength: 1, maxLength: MAX_TOPIC_NAME },
      promptIds: {
        type: 'array',
        maxItems: MAX_PROMPTS_PER_GROUP,
        uniqueItems: true,
        items: { type: 'string', pattern: '^query-[a-f0-9]{16}$' },
      },
    },
  },
};
